import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, eur } from "../lib/api";
import {
  Calculator, HardHat, FileText, Users, Package, Truck, Wrench, Map as MapIcon,
  ShieldAlert, CheckSquare, Bot, DatabaseZap, Euro, TrendingUp, AlertTriangle, Clock, ShoppingCart,
} from "lucide-react";

const ENTRIES = [
  { to: "/calculs", label: "CALCULS", icon: Calculator, tid: "entry-calculs" },
  { to: "/chantiers", label: "CHANTIERS", icon: HardHat, tid: "entry-chantiers" },
  { to: "/devis", label: "DEVIS", icon: FileText, tid: "entry-devis" },
  { to: "/simulateur", label: "SIMULATEUR COMMANDE", icon: ShoppingCart, tid: "entry-simulateur" },
  { to: "/crm", label: "CRM", icon: Users, tid: "entry-crm" },
  { to: "/fournisseurs", label: "FOURNISSEURS & PRIX", icon: Package, tid: "entry-fournisseurs" },
  { to: "/sous-traitants", label: "SOUS-TRAITANTS", icon: Truck, tid: "entry-sous-traitants" },
  { to: "/materiel", label: "MATÉRIEL & VÉHICULES", icon: Wrench, tid: "entry-materiel" },
  { to: "/carte", label: "CARTOGRAPHIE", icon: MapIcon, tid: "entry-carte" },
  { to: "/risques", label: "RISQUES DROM", icon: ShieldAlert, tid: "entry-risques" },
  { to: "/taches", label: "TÂCHES", icon: CheckSquare, tid: "entry-taches" },
  { to: "/assistant", label: "TECA ASSISTANT", icon: Bot, tid: "entry-assistant" },
  { to: "/sources", label: "SOURCES & AUDIT", icon: DatabaseZap, tid: "entry-sources" },
];

const KPIS = [
  { k: "ca_devise", l: "CA devisé", money: true, icon: Euro, tid: "kpi-ca-devise" },
  { k: "ca_signe", l: "CA signé", money: true, icon: TrendingUp, tid: "kpi-ca-signe" },
  { k: "devis_en_attente", l: "Devis en attente", icon: Clock, tid: "kpi-devis-attente" },
  { k: "marge_prevue", l: "Marge prévue", money: true, icon: TrendingUp, tid: "kpi-marge" },
  { k: "chantiers_actifs", l: "Chantiers actifs", icon: HardHat, tid: "kpi-chantiers-actifs" },
  { k: "chantiers_a_demarrer", l: "Chantiers à démarrer", icon: HardHat, tid: "kpi-chantiers-demarrer" },
  { k: "prospects_a_relancer", l: "Prospects à relancer", icon: Users, tid: "kpi-prospects" },
  { k: "opportunites", l: "Opportunités", icon: TrendingUp, tid: "kpi-opportunites" },
  { k: "materiel_disponible", l: "Matériel disponible", icon: Wrench, tid: "kpi-mat-dispo" },
  { k: "materiel_immobilise", l: "Matériel immobilisé", icon: AlertTriangle, tid: "kpi-mat-immo" },
  { k: "prestataires_non_conformes", l: "Prestataires non conformes", icon: AlertTriangle, tid: "kpi-prestataires" },
  { k: "taches_urgentes", l: "Tâches en cours", icon: CheckSquare, tid: "kpi-taches" },
];

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div className="rounded-2xl overflow-hidden relative">
        <img src="https://images.pexels.com/photos/37837081/pexels-photo-37837081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt="Chantier TECA" className="w-full h-40 sm:h-52 object-cover" />
        <div className="absolute inset-0 bg-slate-900/60 flex items-end p-5">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white uppercase tracking-tight">Tableau de bord</h1>
            <p className="text-amber-400 text-sm font-semibold">TECA SOLUTIONS TP — Direction</p>
          </div>
        </div>
      </div>

      <section>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3" data-testid="home-entries">
          {ENTRIES.map((e) => (
            <Link key={e.to} to={e.to} data-testid={e.tid}
              className="flex flex-col items-center justify-center gap-2 p-4 min-h-[96px] rounded-xl bg-card border-2 border-border hover:border-amber-500 hover:shadow-lg transition-all">
              <e.icon className="h-7 w-7 text-amber-600" />
              <span className="font-display font-bold text-sm text-center tracking-wide">{e.label}</span>
            </Link>
          ))}
        </div>
      </section>

      <section>
        <h2 className="font-display text-xl font-bold uppercase mb-3">Indicateurs</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="kpi-grid">
          {KPIS.map((k) => (
            <div key={k.k} data-testid={k.tid} className="bg-card rounded-xl border border-border p-4 shadow-sm">
              <div className="flex items-center gap-2 text-muted-foreground mb-1">
                <k.icon className="h-4 w-4" />
                <span className="text-[11px] font-bold uppercase tracking-wide">{k.l}</span>
              </div>
              <div className="font-mono-num text-xl sm:text-2xl font-bold">
                {data ? (k.money ? eur(data[k.k]) : data[k.k]) : "—"}
              </div>
            </div>
          ))}
        </div>
      </section>

      {data?.pipeline?.length > 0 && (
        <section>
          <h2 className="font-display text-xl font-bold uppercase mb-3">Pipeline devis</h2>
          <div className="bg-card rounded-xl border border-border divide-y divide-border" data-testid="pipeline-list">
            {data.pipeline.map((p) => (
              <div key={p.statut} className="flex items-center justify-between px-4 py-3">
                <span className="font-semibold text-sm">{p.statut} <span className="text-muted-foreground font-normal">({p.count})</span></span>
                <span className="font-mono-num font-bold">{eur(p.total)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
